import type {BotIdProtectedRoute} from './types';

/**
 * Paths the BotID client attaches a verification token to. Two kinds:
 *
 * 1. Every page POST (`/` and `/*`): the client-credentials token action is a
 *    Server Action, which rides the current page path as a POST — so the page
 *    must be covered for the browser to attach a token the action can verify.
 * 2. The explicit API routes that call `rejectBots()`. Subsumed by the page
 *    catch-all, but kept so narrowing the catch-all can't silently drop them.
 *
 * Anything not listed sends no token, so `checkBotId()` treats it as a bot —
 * it fails closed, never open.
 */
export const BOT_PROTECTED_ROUTES: BotIdProtectedRoute[] = [
  {path: '/', method: 'POST'},
  {path: '/*', method: 'POST'},
  {path: '/api/portal/create-session', method: 'POST'},
  {path: '/api/portal/check-user-status', method: 'POST'},
  {path: '/api/onboarding-register', method: 'POST'},
  {path: '/api/advisor/*/bookings', method: 'POST'},
  {path: '/api/advice-request/*/bookings', method: 'POST'},
  {path: '/api/agent/chat', method: 'POST'},
  {path: '/api/agent/refresh-availability', method: 'POST'},
  {path: '/api/agent/warmup', method: 'POST'},
  {path: '/api/agent/feedback', method: 'POST'},
];

/** A route handler containing any of these is considered guarded. */
export const GUARD_CALLS = [
  'rejectBots(',
  'validatePortalUserRequest(',
  'getPortalUserCookie(',
  'CRON_SECRET',
];

/** Routes that answer without a session or BotID, and why that's correct. */
export const INTENTIONALLY_PUBLIC_ROUTES: Record<string, string> = {
  'api-docs/[partner]/route.ts': 'Static partner API docs',
  'api-docs/internal/route.ts': 'Static API docs',
  'api/openapi/[partner]/route.ts': 'Static partner OpenAPI spec',
  'api/openapi/internal/route.ts': 'Static OpenAPI spec',
  'api/referrals/[partner]/auth/route.ts':
    'Partner server-to-server token exchange, checks client_secret',
  'api/referrals/[partner]/form/route.ts':
    'Partner server-to-server submit, checks the partner JWT',
  'api/referrals/[partner]/leads/route.ts':
    'Partner server-to-server submit, checks the partner JWT',
  'referrals/[userIdentifier]/redirect/route.ts':
    'Emailed magic link: random UUID v4, expires after a month, top-level navigation only',
};

/**
 * Block comments and whole-line `//` comments, stripped before matching so a
 * commented-out guard doesn't count. A guard named only inside a string still
 * would; this is a text check, not a parser.
 */
export const COMMENT_PATTERN = /\/\*[\s\S]*?\*\/|^\s*\/\/.*$/gm;
